<script setup lang="ts">
import CommonMonsterDetail from '@/components/common-monster-detail/CommonMonsterDetail.vue'
import { useRoomStore } from '@/stores/modules/room'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { onMounted, ref } from 'vue'
import { crackRank } from '../data'
import { getSkillNameText } from '@/core/skill'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ monster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'showRoom'])

const rank = ref(1)
const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}skill-corridor-rank`
function handleChangeRank() {
  localStorage.setItem(localKey, String(rank.value))
}

function handleHuntSkillCorridorMonster() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.skillCorridorTimes <= 0) return ElMessage.warning('无次数时无法进行探索')
  if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法进行探索')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">探索</span>【<span class="mp">秘技回廊</span>】吗？（本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntSkillCorridorMonster', data: { rank: rank.value } })
    emits('getMonster', res)
    ElMessage.success('探索成功')
  })
}

async function handleStartSkillCorridor() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法进行战斗')
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="mp">秘技回廊</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startSkillCorridorFight' })
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'skillCorridor' })
  }
}

function handleRefreshSkillCorridor() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  const needDiamond = userStore.vipLevel ? 100 : 200
  if (userStore.diamond < needDiamond) return ElMessage.warning(`重新探索需要 ${needDiamond}钻石`)
  if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法进行探索')
  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探索怪物')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="hp">放弃</span>探索当前回廊并进行重新<span class="info">探索</span>吗？（消耗 <span class="diamond">${needDiamond}</span>钻石，本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshSkillCorridorMonster', data: { rank: rank.value } })
    userStore.diamond -= needDiamond
    emits('getMonster', res)
    ElMessage.success('重新探索成功')
  })
}

function handleOpenRoom() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法进行战斗')
  emits('showRoom')
}

onMounted(() => {
  const localRank = localStorage.getItem(localKey)
  if (localRank) rank.value = Number(localRank)
})
</script>

<template>
  <div class="border-wrap skill-corridor">
    <h4 class="mp">
      秘技回廊
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>秘技回廊说明</h4>
            <p>1. 秘技回廊<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 通过<span class="info">探索</span>，您会随机遭遇一只怪物，同时得知<span class="relic">技能线索</span>以及<span class="info"
                >启迪卷轴出率</span
              >。
            </p>
            <p>3. 通过秘技回廊战斗您将必定获得 对应<span class="info">线索</span>的 1本技能书，并有概率获得<span class="mp">启迪卷轴</span>。</p>
            <p>
              4. <span class="info">难度</span>越高的怪物挑战难度越大，同时随难度提升，探索的<span class="mp">技能线索</span
              >中的高阶级概率将提升；线索中的技能书品质颜色仅与阶级相关（<span class="normal">1阶</span>、<span class="race">2阶</span>、<span
                class="epic"
                >3阶</span
              >、<span class="legend">4阶</span>、<span class="antiquity">5阶</span>）。
            </p>
            <p>5. 实际获取的技能书品质与技能线索示例无关，根据挑战难度将增加高品质技能的爆率。</p>
            <p>6. <span class="antiquity">噩梦</span>难度中有极低概率获取<span class="antiquity">远古</span>技能书。</p>
            <p>7. 秘技回廊支持<span class="info">双人组队</span>，当组队挑战时，怪物血量增加<span class="grow">0.7</span>倍。</p>
            <p>8. 战斗失败时，<span class="grow">不会消耗</span>奖励次数；当奖励次数用尽时，您无法探索及领取奖励，但仍可协助其他玩家。</p>
            <p>9. 副本各项信息如表格所示：</p>
            <table border style="width: 100%; text-align: center">
              <tr>
                <td width="150">参考信息</td>
                <td class="normal">简单</td>
                <td class="race">普通</td>
                <td class="epic">困难</td>
                <td class="antiquity">噩梦</td>
              </tr>
              <tr>
                <td>虚化值</td>
                <td>300</td>
                <td>400</td>
                <td>500</td>
                <td>600</td>
              </tr>
              <tr>
                <td>最高技能书品质</td>
                <td class="epic">史诗</td>
                <td class="epic">史诗</td>
                <td class="legend">传说</td>
                <td class="antiquity">远古</td>
              </tr>
              <tr>
                <td>最高技能阶级</td>
                <td class="epic">3</td>
                <td class="legend">4</td>
                <td class="antiquity">5</td>
                <td class="antiquity">5</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center">
      难度：
      <el-select size="small" v-model="rank" :class="crackRank[rank].class" style="width: 90px" @change="handleChangeRank">
        <el-option v-for="item of [1, 2, 3, 4]" :key="item" :label="crackRank[item].name" :value="item">
          <span :class="crackRank[item].class">{{ crackRank[item].name }}</span>
        </el-option>
      </el-select>
    </p>
    <p>
      <span>剩余奖励次数：{{ userStore.skillCorridorTimes }}</span>
    </p>
    <p>
      当前怪物：
      <CommonMonsterDetail :monster="monster" icon="🪔" :teleported="true">
        <template #pre>
          <p v-if="monster?.rank">
            难度：<span :class="crackRank[monster.rank]?.class">{{ crackRank[monster.rank]?.name }}</span>
          </p>
          <p v-if="monster?.skillIdList?.length">技能线索：<span v-html="getSkillNameText(monster.skillIdList)"></span></p>
          <p v-if="monster?.skillEnlightenmentScrollRate">
            启迪卷轴概率：<span class="mp">{{ monster.skillEnlightenmentScrollRate }}%</span>
          </p>
        </template>
      </CommonMonsterDetail>
    </p>
    <div style="margin: 10px 0">
      <div v-show="!monster?.name">
        <el-button type="warning" plain @click="handleHuntSkillCorridorMonster" :disabled="userStore.soulType <= 3">
          {{ userStore.soulType <= 3 ? '二觉后解锁' : '探索' }}
        </el-button>
        <el-button type="primary" plain :disabled="userStore.soulType <= 3" @click="handleOpenRoom">组队战斗</el-button>
      </div>
      <div v-show="monster?.name">
        <el-button type="success" plain @click="() => handleStartSkillCorridor()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshSkillCorridor">重新探索</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  .normal {
    .el-select__selected-item {
      span {
        color: #888084;
      }
    }
  }
  .race {
    .el-select__selected-item {
      span {
        color: #3e62ed;
      }
    }
  }
  .epic {
    .el-select__selected-item {
      span {
        color: #8d4bbb;
      }
    }
  }
  .antiquity {
    .el-select__selected-item {
      span {
        color: #f05654;
      }
    }
  }
}
</style>
